import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Eye, EyeOff } from 'lucide-react';
import { checkLogin, change_MyData, setOtpVerified } from '../../../Common/store.js';
import { terminalLogin } from '../../api/cp';
import { getOneHourExpiry } from '../../../Common/utils/sessionExpiry.js';
import { useTooltip } from '../../../Common/context/TooltipContext';
import { getAppLogoAssetUrl, getAppLoginLogoStyle } from '../../../Common/constants/adminImages';
import PhpPayLoginShell from '../../../Common/components/Login/PhpPayLoginShell';
import { BRAND_COPYRIGHT, LOGIN_LAYOUT, SERVICE_BRAND_NAME, brandPageAttributes } from '../../../Common/config/brandConfig';

const SESSION_EXPIRY_KEY = 'session_expiry';
const SAVED_TERMINAL_ID_KEY = 'app_saved_terminal_id';
const SAVE_ID_CHECKED_KEY = 'app_save_id_checked';
const SAVED_TERMINAL_NAME_KEY = 'app_saved_terminal_name';
const CP_MALL_URL = (process.env.REACT_APP_CP_MALL_URL || "").trim();
const CP_MALL_LABEL = (process.env.REACT_APP_CP_MALL_LABEL || "Mall 바로가기").trim();

/**
 * 앱 로그인
 * - API: POST /api/cp/terminal/login
 * - Body: { terminalId, password }
 */
function AppLogin() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { resetShowAll } = useTooltip();

  const [id, setId] = useState('');
  const [password, setPassword] = useState('');
  const [saveId, setSaveId] = useState(false);
  const [toast, setToast] = useState({ visible: false, message: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    resetShowAll();
  }, [resetShowAll]);

  // 저장된 아이디/아이디 저장 체크 복원
  useEffect(() => {
    try {
      const checked = localStorage.getItem(SAVE_ID_CHECKED_KEY) === 'true';
      const savedId = localStorage.getItem(SAVED_TERMINAL_ID_KEY) || '';
      setSaveId(checked);
      if (checked && savedId) setId(savedId);
    } catch (err) {}
  }, []);

  useEffect(() => {
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    return () => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    };
  }, []);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!id.trim() || !password.trim()) {
      setToast({ visible: true, message: '아이디와 패스워드를 입력해주세요.' });
      setTimeout(() => setToast((prev) => ({ ...prev, visible: false })), 2000);
      return;
    }
    try {
      setIsLoading(true);
      // 앱 로그인: 영업자 로그인 API 사용
      const data = await terminalLogin({ terminalId: id.trim(), password });

      const mustChangePassword = data?.must_change_password === true || data?.mustChangePassword === true;
      const terminal = data?.terminal ?? data;
      const demoMode = data?.demoMode === true || data?.demo_mode === true || terminal?.isDemo === true || terminal?.is_demo === true;
      const name = terminal?.name || '영업자';
      try {
        if (demoMode) sessionStorage.setItem('terminal_demo_mode', 'true');
        else sessionStorage.removeItem('terminal_demo_mode');
      } catch (_) {}

      // 수기결제 화면에서 영업자명 자동 주입용
      try {
        localStorage.setItem(SAVED_TERMINAL_NAME_KEY, name);
      } catch (err) {}

      dispatch(checkLogin(true));
      dispatch(setOtpVerified(true));
      dispatch(
        change_MyData({
          name,
          type: 'app',
          cpId: terminal?.terminalId ?? terminal?.terminal_id ?? terminal?.id ?? id.trim(),
          status: terminal?.status,
          isDemo: terminal?.isDemo === true || terminal?.is_demo === true,
          demoMode,
        }),
      );

      const expiry = String(getOneHourExpiry());
      try {
        sessionStorage.setItem(SESSION_EXPIRY_KEY, expiry);
        localStorage.setItem('ls_session_expiry', expiry);
      } catch (err) {}

      // 아이디 저장: 체크 시 저장, 미체크 시 삭제
      try {
        if (saveId) {
          localStorage.setItem(SAVED_TERMINAL_ID_KEY, id.trim());
          localStorage.setItem(SAVE_ID_CHECKED_KEY, 'true');
        } else {
          localStorage.removeItem(SAVED_TERMINAL_ID_KEY);
          localStorage.setItem(SAVE_ID_CHECKED_KEY, 'false');
        }
      } catch (err) {}

      navigate('/app/dashboard', {
        state: {
          showPasswordChangeNotice: mustChangePassword,
        },
      });
    } catch (e) {
      const message = e?.response?.data?.msg || e?.message || '로그인에 실패했습니다.';
      setToast({ visible: true, message });
      setTimeout(() => setToast((prev) => ({ ...prev, visible: false })), 2000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleSubmit();
  };

  if (LOGIN_LAYOUT === 'phpay-split') {
    return (
      <PhpPayLoginShell
        logoSrc={getAppLogoAssetUrl()}
        logoStyle={{ width: '112px', height: 'auto' }}
        portalLabel="영업자 전용"
        title="영업자 로그인"
        description="영업자 계정으로 결제 업무를 시작해보세요."
        heroTitle="결제를 더 간편하게"
        heroDescription="phPay 영업자 전용 결제 서비스"
        variant="app"
      >
        {toast.visible && (
          <div className="phpay-login-toast" role="alert">
            {toast.message}
          </div>
        )}

        <form onSubmit={handleSubmit} className="phpay-login-form">
          <div className="phpay-login-field">
            <label htmlFor="phpay-app-id">아이디</label>
            <input
              id="phpay-app-id"
              type="text"
              placeholder="아이디"
              value={id}
              onChange={(e) => setId(e.target.value)}
              autoComplete="username"
            />
          </div>
          <div className="phpay-login-field">
            <label htmlFor="phpay-app-password">비밀번호</label>
            <div className="phpay-login-input-wrap">
              <input
                id="phpay-app-password"
                type={showPassword ? 'text' : 'password'}
                placeholder="비밀번호"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="phpay-login-password-toggle"
                aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 보기'}
              >
                {showPassword ? <EyeOff size={19} /> : <Eye size={19} />}
              </button>
            </div>
          </div>

          <label className="phpay-login-save-id" htmlFor="phpay-app-save-id">
            <input
              id="phpay-app-save-id"
              type="checkbox"
              checked={saveId}
              onChange={() => setSaveId((prev) => !prev)}
            />
            아이디 저장
          </label>

          <button
            type="submit"
            disabled={isLoading}
            className="phpay-login-primary-button"
          >
            {isLoading ? '로그인 중...' : '로그인'}
          </button>
          {CP_MALL_URL && (
            <a
              href={CP_MALL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="phpay-login-secondary-button phpay-login-mall-button"
            >
              {CP_MALL_LABEL}
            </a>
          )}
        </form>
      </PhpPayLoginShell>
    );
  }

  return (
    <div
      {...brandPageAttributes}
      style={{
        height: '100dvh',
        minHeight: '100dvh',
        maxHeight: '100dvh',
        width: '100%',
        maxWidth: '768px',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(160deg, var(--brand-gradient-start) 0%, var(--brand-gradient-mid) 60%, var(--brand-gradient-end) 100%)',
        padding: '40px 20px',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      <style>
        {`
          input:focus, select:focus, textarea:focus {
            outline: none !important;
            -webkit-tap-highlight-color: transparent;
            border-color: #E5E7EB !important;
            box-shadow: none !important;
          }
          input, select, textarea { outline: none !important; }
          input:focus-visible {
            outline: none !important;
            border-color: #E5E7EB !important;
            box-shadow: none !important;
          }
          button:focus { outline: none !important; }
          button:active { outline: none !important; background-color: inherit !important; }
          #app-login-btn:focus {
            outline: none !important;
            box-shadow: 0 4px 12px rgba(var(--brand-primary-rgb), 0.3) !important;
          }
          #app-login-btn:active { outline: none !important; }
          @keyframes dotWave {
            0%, 60%, 100% { transform: translateY(0); opacity: 0.7; }
            30% { transform: translateY(-8px); opacity: 1; }
          }
          @keyframes slideDownToast {
            from { opacity: 0; transform: translateX(-50%) translateY(-100%); }
            to { opacity: 1; transform: translateX(-50%) translateY(0); }
          }
        `}
      </style>

      {/* 로그인 패널 */}
      <div
        style={{
          position: 'relative',
          width: '85%',
          maxWidth: '400px',
          backgroundColor: '#FFFFFF',
          borderRadius: '20px',
          padding: '32px 24px',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: '-45px',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <img src={getAppLogoAssetUrl()} alt={SERVICE_BRAND_NAME} style={getAppLoginLogoStyle({ width: '140px', height: 'auto' })} />
        </div>

        <h2
          style={{
            fontSize: '24px',
            fontWeight: 700,
            color: '#111827',
            textAlign: 'center',
            margin: '0 0 8px 0',
          }}
        >
          의약품 결제 서비스
        </h2>
        <div
          style={{
            fontSize: '14px',
            color: '#6B7280',
            textAlign: 'center',
            marginBottom: '24px',
          }}
        >
          영업자 로그인
        </div>

        <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
          <div style={{ marginBottom: '12px' }}>
            <input
              type="text"
              id="app-login-id"
              placeholder="아이디"
              value={id}
              onChange={(e) => setId(e.target.value)}
              style={{
                width: '100%',
                padding: '14px 16px',
                border: '1px solid #E5E7EB',
                borderRadius: '12px',
                fontSize: '16px',
                backgroundColor: '#FFFFFF',
                boxSizing: 'border-box',
                outline: 'none',
              }}
            />
          </div>
          <div style={{ marginBottom: '16px', position: 'relative' }}>
            <input
              type={showPassword ? 'text' : 'password'}
              id="app-login-pw"
              autoComplete="off"
              placeholder="패스워드"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={handleKeyPress}
              style={{
                width: '100%',
                padding: '14px 44px 14px 16px',
                border: '1px solid #E5E7EB',
                borderRadius: '12px',
                fontSize: '16px',
                backgroundColor: '#FFFFFF',
                boxSizing: 'border-box',
                outline: 'none',
              }}
            />
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 보기'}
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                border: 'none',
                background: 'transparent',
                padding: '4px',
                cursor: 'pointer',
                color: '#6B7280',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </form>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '16px',
          }}
        >
          <input
            type="checkbox"
            id="app-saveId"
            checked={saveId}
            onChange={() => setSaveId((prev) => !prev)}
            style={{
              width: '18px',
              height: '18px',
              marginRight: '8px',
              cursor: 'pointer',
            }}
          />
          <label
            htmlFor="app-saveId"
            style={{
              fontSize: '14px',
              color: '#374151',
              cursor: 'pointer',
            }}
          >
            아이디 저장
          </label>
        </div>

        <button
          id="app-login-btn"
          type="button"
          onClick={() => handleSubmit()}
          disabled={isLoading}
          style={{
            width: '100%',
            padding: '16px',
            backgroundColor: isLoading ? '#9CA3AF' : 'var(--terminal-login-primary, #3182F6)',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '12px',
            fontSize: '16px',
            fontWeight: 600,
            cursor: isLoading ? 'not-allowed' : 'pointer',
            boxShadow: isLoading ? 'none' : '0 4px 12px rgba(var(--brand-primary-rgb), 0.3)',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            height: '52px',
            outline: 'none',
            WebkitTapHighlightColor: 'transparent',
          }}
          onMouseEnter={(e) => {
            if (!isLoading) e.currentTarget.style.backgroundColor = 'var(--terminal-login-hover, #2563EB)';
          }}
          onMouseLeave={(e) => {
            if (!isLoading) e.currentTarget.style.backgroundColor = 'var(--terminal-login-primary, #3182F6)';
          }}
          onMouseDown={(e) => {
            if (!isLoading) e.currentTarget.style.backgroundColor = 'var(--terminal-login-active, #1D4ED8)';
          }}
          onMouseUp={(e) => {
            if (!isLoading) e.currentTarget.style.backgroundColor = 'var(--terminal-login-primary, #3182F6)';
          }}
        >
          {isLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <div
                style={{
                  width: '7px',
                  height: '7px',
                  borderRadius: '50%',
                  backgroundColor: '#FFFFFF',
                  animation: 'dotWave 1.4s ease-in-out infinite',
                  animationDelay: '0s',
                }}
              />
              <div
                style={{
                  width: '7px',
                  height: '7px',
                  borderRadius: '50%',
                  backgroundColor: '#FFFFFF',
                  animation: 'dotWave 1.4s ease-in-out infinite',
                  animationDelay: '0.2s',
                }}
              />
              <div
                style={{
                  width: '7px',
                  height: '7px',
                  borderRadius: '50%',
                  backgroundColor: '#FFFFFF',
                  animation: 'dotWave 1.4s ease-in-out infinite',
                  animationDelay: '0.4s',
                }}
              />
            </div>
          ) : (
            '로그인'
          )}
        </button>

        <div
          style={{
            position: 'absolute',
            bottom: '-40px',
            left: '50%',
            transform: 'translateX(-50%)',
            textAlign: 'center',
            width: '100%',
          }}
        >
          <p style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>
            {BRAND_COPYRIGHT}
          </p>
        </div>
      </div>

      {toast.visible && (
        <div
          style={{
            position: 'fixed',
            top: '10px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '100%',
            maxWidth: '400px',
            padding: '0 16px',
            boxSizing: 'border-box',
            zIndex: 3000,
            display: 'flex',
            justifyContent: 'center',
            animation: 'slideDownToast 0.3s ease-out',
            pointerEvents: 'none',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '12px 16px',
              borderRadius: '12px',
              backgroundColor: '#FFFFFF',
              color: '#111827',
              fontSize: '14px',
              fontWeight: 500,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              pointerEvents: 'auto',
            }}
          >
            <div
              style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                backgroundColor: '#EF4444',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <span style={{ color: '#FFFFFF', fontSize: '14px' }}>!</span>
            </div>
            <span>{toast.message}</span>
          </div>
        </div>
      )}

      {/* <div
        style={{
          position: 'absolute',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <img
          src="/img/ntoss_logo_dark.png"
          alt="NToss Logo"
          style={{ width: '100px', height: 'auto' }}
        />
      </div> */}
    </div>
  );
}

export default AppLogin;
